// Edit one short piece of text in place: a label with a pencil beside it that
// turns into an input with a confirm and a cancel.
//
// It is controlled from outside and knows nothing about what it is naming.
// `onSave(next)` is the whole contract: the caller performs the write and
// returns its promise. This component closes on a resolved promise and STAYS
// OPEN WITH THE TYPED TEXT on a rejected one, because a failed save that also
// discards what the person wrote makes them type it twice.
//
// Chakra ships `Editable`/`useEditableControls`, and this app uses it once, in
// Appointments/AppointmentModal/MeetingLinks.js. It is not used here: Editable
// owns its own value and commits on blur, which fights the three things this
// control needs — a spinner while a request is in flight, keeping the draft on
// failure, and one row in edit mode at a time. The behaviour instead mirrors
// the same control in blhClinicApp/src/components/shared/PaymentMethodsManager.jsx,
// so naming a card behaves identically for staff and for patients.

import { useState } from "react";
import { HStack, IconButton, Input } from "@chakra-ui/react";
import { FiCheck, FiEdit2, FiX } from "react-icons/fi";

type Props = {
  /** Current stored text. Seeds the draft when editing starts. */
  value: string;
  /** Rendered in display mode — usually a formatted label, not raw `value`. */
  children: React.ReactNode;
  /** Performs the write. Return the promise so this control can await it. */
  onSave: (next: string) => void | Promise<unknown>;
  placeholder?: string;
  maxLength?: number;
  /** Describes what is being named, for the pencil's screen-reader label. */
  editLabel?: string;
  inputWidth?: string;
};

export const InlineEdit = ({
  value,
  children,
  onSave,
  placeholder,
  maxLength = 40,
  editLabel = "Edit name",
  inputWidth = "180px",
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState("");

  const startEditing = () => {
    setDraft(value || "");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setDraft("");
  };

  const save = async () => {
    // Enter and the confirm button are the same action; without this a second
    // keypress fires a second write while the first is still in flight.
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onSave(draft);
      setIsEditing(false);
      setDraft("");
    } catch {
      // Deliberately silent: the caller owns the error message (a toast, an
      // inline line). All this control does is keep the draft on screen.
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <HStack spacing={1}>
        {children}
        <IconButton
          aria-label={editLabel}
          icon={<FiEdit2 />}
          size="xs"
          variant="ghost"
          color="#6b7280"
          onClick={startEditing}
        />
      </HStack>
    );
  }

  return (
    <HStack spacing={1}>
      <Input
        autoFocus
        size="sm"
        maxLength={maxLength}
        w={inputWidth}
        borderRadius="8px"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") cancelEditing();
        }}
      />
      <IconButton
        aria-label="Save name"
        icon={<FiCheck />}
        size="xs"
        variant="ghost"
        isLoading={isSaving}
        onClick={save}
      />
      <IconButton
        aria-label="Cancel"
        icon={<FiX />}
        size="xs"
        variant="ghost"
        isDisabled={isSaving}
        onClick={cancelEditing}
      />
    </HStack>
  );
};
